// ═══════════════════════════════════════════════════════════
// Request List — filterable, sortable request queue
// ═══════════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { IconSearch, IconFilter, IconPlus, IconLoader2, IconCircleDot } from '@tabler/icons-react';
import { CLOCK_COLORS, formatDaysRemaining } from '../../../lib/deadlineClock';
import { formatDate } from '../../../lib/format';
import { useRequestStore } from '../../../store/useRequestStore';
import { useAuthStore } from '../../../store/authStore';
import { listDepartments } from '../../../api/departments';
import type { FoiaRequest } from '../../../api/requests';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', acknowledged: 'Acknowledged',
  inProgress: 'In Progress', awaitingRequester: 'Awaiting Requester',
  awaitingPayment: 'Awaiting Payment', pendingReview: 'Pending Review',
  completed: 'Completed', denied: 'Denied', withdrawn: 'Withdrawn', onHold: 'On Hold',
};

const STATUS_STYLE: Record<string, { background: string; color: string }> = {
  submitted:        { background: 'var(--cds-cerulean-50)',  color: 'var(--cds-cerulean-700)' },
  acknowledged:     { background: 'var(--cds-green-50)',     color: 'var(--cds-green-700)'    },
  inProgress:       { background: 'var(--cds-blurple-50)',   color: 'var(--cds-blurple-700)'  },
  awaitingRequester:{ background: 'var(--cds-yellow-50)',    color: 'var(--cds-yellow-700)'   },
  awaitingPayment:  { background: 'var(--cds-yellow-50)',    color: 'var(--cds-yellow-700)'   },
  pendingReview:    { background: 'var(--cds-violet-50)',    color: 'var(--cds-violet-700)'   },
  completed:        { background: 'var(--cds-green-50)',     color: 'var(--cds-green-700)'    },
  denied:           { background: 'var(--cds-red-50)',       color: 'var(--cds-red-700)'      },
  withdrawn:        { background: 'var(--cds-gray-50)',      color: 'var(--cds-gray-700)'     },
  onHold:           { background: 'var(--cds-gray-50)',      color: 'var(--cds-gray-700)'     },
  draft:            { background: 'var(--cds-gray-50)',      color: 'var(--cds-gray-700)'     },
};

const PRIORITY_STYLE: Record<string, { color: string }> = {
  urgent: { color: 'var(--cds-red-700)'    },
  high:   { color: 'var(--cds-orange-700)' },
  normal: { color: 'var(--cds-gray-600)'   },
};

const ALL_STATUSES = ['submitted', 'acknowledged', 'inProgress', 'awaitingRequester', 'awaitingPayment', 'pendingReview', 'completed', 'denied'];
const NEEDS_ACTION_STATUSES = new Set(['submitted', 'pendingReview', 'awaitingPayment']);
const CLOSED_STATUSES = new Set(['completed', 'denied', 'withdrawn']);

export default function RequestList() {
  const navigate = useNavigate();
  const { requests, loading, error, fetchRequests } = useRequestStore();
  const user = useAuthStore(s => s.user);

  const isDeptStaff = user?.staffRole === 'department_staff' && !!user?.departmentId;

  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') ?? '';

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    initialFilter === 'submitted'     ? 'submitted'     :
    initialFilter === 'pendingReview' ? 'pendingReview' :
    initialFilter === 'awaitingPayment' ? 'awaitingPayment' : ''
  );
  const [needsActionFilter, setNeedsActionFilter] = useState(initialFilter === 'needs-action');
  const [clockFilter, setClockFilter] = useState<string>(
    initialFilter === 'overdue' ? 'overdue' : initialFilter === 'due-soon' ? 'due-soon' : ''
  );
  const [deptFilter, setDeptFilter] = useState(
    isDeptStaff ? (user.departmentId as string) : (initialFilter === 'unassigned' ? '__unassigned__' : '')
  );
  const [showClosed, setShowClosed] = useState(initialFilter === 'completed' || initialFilter === 'denied');
  const [deptMap, setDeptMap] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchRequests({
      includeClosd: true,
      ...(isDeptStaff && user?.departmentId ? { departmentId: user.departmentId } : {}),
    });
    listDepartments().then(depts => {
      const map: Record<string, string> = {};
      for (const d of depts) map[String(d.recordId)] = d.name;
      setDeptMap(map);
    });
  }, [fetchRequests, isDeptStaff, user?.departmentId]);

  // Departments that actually appear in data, resolved to names for the filter dropdown
  const allDepts = useMemo(() => {
    const ids = [...new Set(requests.map(r => r.assignedDeptId).filter(Boolean) as string[])];
    return ids.map(id => ({ id, name: deptMap[id] ?? id })).sort((a, b) => a.name.localeCompare(b.name));
  }, [requests, deptMap]);

  const filtered = useMemo((): FoiaRequest[] => {
    return requests.filter(r => {
      const isClosed = CLOSED_STATUSES.has(r.status);
      if (!showClosed && isClosed) return false;
      if (needsActionFilter && !NEEDS_ACTION_STATUSES.has(r.status)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!r.requestNumber.toLowerCase().includes(q) && !r.subject.toLowerCase().includes(q)) return false;
      }
      if (statusFilter && r.status !== statusFilter) return false;
      if (deptFilter === '__unassigned__' && r.assignedDeptId) return false;
      if (deptFilter && deptFilter !== '__unassigned__' && r.assignedDeptId !== deptFilter) return false;
      if (clockFilter === 'overdue' && !r.isOverdue) return false;
      if (clockFilter === 'due-soon' && r.clockStatus !== 'due-soon') return false;
      if (clockFilter === 'paused' && !r.isPaused) return false;
      return true;
    }).sort((a, b) => {
      // Needs-action statuses bubble to top
      const aAction = NEEDS_ACTION_STATUSES.has(a.status) ? 0 : 1;
      const bAction = NEEDS_ACTION_STATUSES.has(b.status) ? 0 : 1;
      if (aAction !== bAction) return aAction - bAction;
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      return (a.daysRemaining ?? 999) - (b.daysRemaining ?? 999);
    });
  }, [requests, search, statusFilter, deptFilter, showClosed, clockFilter, needsActionFilter]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>
            Requests
          </h1>
          <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '4px 0 0' }}>
            {loading ? 'Loading…' : error ? `Error: ${error}` : `${filtered.length} ${filtered.length === 1 ? 'request' : 'requests'}${!showClosed ? ' · open only' : ''}`}
          </p>
        </div>
        <Link to="/staff/requests/new" className="btn btn-primary btn-sm" style={{ gap: 5 }}>
          <IconPlus size={13} /> New Request
        </Link>
      </div>

      {/* Quick filter chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {(() => {
          const needsActionCount = requests.filter(r => NEEDS_ACTION_STATUSES.has(r.status)).length;
          return (
            <button
              onClick={() => {
                const next = !needsActionFilter;
                setNeedsActionFilter(next);
                if (next) {
                  // activating: clear all other filters for a clean view
                  setStatusFilter('');
                  setClockFilter('');
                  if (!isDeptStaff) setDeptFilter('');
                } else {
                  // deactivating: restore overdue/due-soon if originally set from URL
                }
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 14px', borderRadius: 99, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                border: needsActionFilter ? 'none' : '1px solid var(--color-neutral-200)',
                background: needsActionFilter ? 'var(--cds-red-700)' : 'white',
                color: needsActionFilter ? 'white' : 'var(--color-neutral-600)',
                boxShadow: needsActionFilter ? '0 1px 4px rgba(177,37,37,0.2)' : 'none',
              }}
            >
              <IconFilter size={12} />
              Needs Attention
              {needsActionCount > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 18, height: 18, padding: '0 4px',
                  background: needsActionFilter ? 'rgba(255,255,255,0.3)' : 'var(--cds-red-700)',
                  color: 'white', borderRadius: 99, fontSize: 10, fontWeight: 700, lineHeight: 1,
                }}>
                  {needsActionCount}
                </span>
              )}
            </button>
          );
        })()}
      </div>

      {/* Filters */}
      <div style={{
        background: 'white', border: '1px solid var(--color-neutral-200)',
        borderRadius: 4, padding: '10px 16px', marginBottom: 16,
        display: 'flex', gap: 8, alignItems: 'center', overflow: 'hidden',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)' }} />
          <input
            type="text"
            className="form-input"
            style={{ paddingLeft: 28, fontSize: 14, width: '100%' }}
            placeholder="Search requests, requester…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-neutral-200)', flexShrink: 0 }} />

        {/* Status filter */}
        <select
          className="form-input"
          style={{ fontSize: 12, padding: '6px 10px', width: 140, flexShrink: 0 }}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {ALL_STATUSES.map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>
          ))}
        </select>

        {/* Department filter */}
        {isDeptStaff ? (
          <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', padding: '6px 10px', background: 'var(--color-neutral-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4, width: 160, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {deptMap[deptFilter] ?? deptFilter}
          </div>
        ) : (
          <select
            className="form-input"
            style={{ fontSize: 12, padding: '6px 10px', width: 160, flexShrink: 0 }}
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
          >
            <option value="">All Departments</option>
            <option value="__unassigned__">Unassigned</option>
            {allDepts.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}

        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-neutral-200)', flexShrink: 0 }} />

        {/* Show closed toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-neutral-600)', cursor: 'pointer', userSelect: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={showClosed}
            onChange={e => setShowClosed(e.target.checked)}
          />
          Include closed
        </label>
      </div>

      {/* Table — flex: 1 fills remaining height; only the rows scroll */}
      <div style={{
        flex: 1, minHeight: 0,
        display: 'flex', flexDirection: 'column',
        background: 'white', border: '1px solid var(--color-neutral-200)',
        borderRadius: 4, overflow: 'hidden',
      }}>
        {loading && requests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-neutral-400)', fontSize: 14 }}>
            <IconLoader2 size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
            <div>Loading requests…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-neutral-400)', fontSize: 14 }}>
            No requests match your filters.
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <table className="data-table data-table-fixed" style={{ width: '100%' }}>
            <colgroup>
              <col style={{ width: '9%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '16%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Request #</th>
                <th>Subject</th>
                <th>Department</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Clock</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const clkStatus = (r.clockStatus ?? 'on-track') as keyof typeof CLOCK_COLORS;
                const clk = CLOCK_COLORS[clkStatus] ?? CLOCK_COLORS['on-track'];
                const isClosed = CLOSED_STATUSES.has(r.status);
                return (
                  <tr key={r.recordId} onClick={() => navigate(`/staff/requests/${r.recordId}`)}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)' }}>
                        {r.requestNumber}
                      </span>
                    </td>
                    <td style={{ overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                        {r.status === 'submitted' && (
                          <IconCircleDot size={10} style={{ color: 'var(--cds-orange-700)', flexShrink: 0 }} title="New submission" />
                        )}
                        {r.status === 'pendingReview' && (
                          <IconCircleDot size={10} style={{ color: '#7C3AED', flexShrink: 0 }} title="Payment received — ready to process" />
                        )}
                        {r.status === 'awaitingPayment' && (
                          <IconCircleDot size={10} style={{ color: 'var(--cds-yellow-700)', flexShrink: 0 }} title="Awaiting payment" />
                        )}
                        <div style={{ fontSize: 14, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.subject}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 1 }}>
                        {formatDate(r.dateSubmitted)} · {r.requestChannel}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-neutral-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.assignedDeptId ? (deptMap[r.assignedDeptId] ?? r.assignedDeptId) : <span style={{ color: 'var(--color-neutral-300)' }}>Unassigned</span>}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 12, fontWeight: 500,
                        ...(STATUS_STYLE[r.status] ?? { background: 'var(--cds-gray-50)', color: 'var(--cds-gray-700)' }),
                      }}>
                        {STATUS_LABELS[r.status] ?? r.status}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 12, fontWeight: r.priority !== 'normal' ? 700 : 400, textTransform: 'capitalize', letterSpacing: '0.02em', ...PRIORITY_STYLE[r.priority] }}>
                        {r.priority ?? 'normal'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 8px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                        background: clk.bg, color: clk.text, border: `1px solid ${clk.border}`,
                        whiteSpace: 'nowrap',
                      }}>
                        {formatDaysRemaining(r.daysRemaining ?? 0, r.isPaused ?? false, isClosed)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
